// Sidebar.js with Dropdown Navigation
import {
  Activity,
  AlertTriangle,
  BarChart3,
  ChartColumnStacked,
  Database,
  ChevronDown,
  ChevronUp,
  CornerLeftUpIcon,
  House,
  Map,
  MapPin,
  Camera,
  ScanLine,
  Search,
  PieChart,
  Settings,
  TrendingUp,
  Flame,
  ClipboardList,
  Video,
  FileText,
  ScanEye,
  X,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "../../context/AuthContext";

export default function Sidebar({
  isOpen,
  onClose,
  userGroups,
  selectedLocation,
  onLocationChange,
  regions = [],
  regionsLoading = false,
}) {
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});

  const toggleDropdown = (itemLabel) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemLabel]: !prev[itemLabel],
    }));
  };

  const getNavItems = () => {
    // Read feature flags from sessionStorage
    let features = {
      analytics: true,
      alpr: false,
      footage: true,
      map: true,
    };
    try {
      const stored = sessionStorage.getItem("userFeatures");
      if (stored) features = JSON.parse(stored);
    } catch (e) {}

    const commonItems = [];

    // Home — always visible
    commonItems.push({ label: "Home", href: "/home", icon: House });

    // Analytics — controlled by feature flag
    if (features.analytics) {
      commonItems.push({
        label: "Analytics",
        href: "/dashboard",
        icon: ChartColumnStacked,
        dropdown: true,
        
        subItems: [
          { label: "Dashboard", href: "/dashboard", icon: Activity },
          {
            label: "Speed Disttributions",
            href: "/kpi/traffic-speed",
            icon: BarChart3,
          },
          {
            label: "Speed Statistics",
            href: "/kpi/speed-stats",
            icon: BarChart3,
          },
          {
            label: "Movement Volumes",
            href: "/kpi/user-count",
            icon: TrendingUp,
          },
          {
            label: "Movement Counts",
            href: "/kpi/turn-count",
            icon: CornerLeftUpIcon,
          },
        ],
      });
    }

    // ALPR — controlled by feature flag
    if (features.alpr) {
      commonItems.push({
        label: "ALPR",
        href: "/alpr-search",
        icon: Camera,
        dropdown: true,
        subItems: [
          { label: "Search", href: "/alpr-search", icon: Search },
          { label: "Hot List", href: "/alpr/hotlist", icon: Flame },
          { label: "Statistics", href: "/alpr/statistics", icon: BarChart3 },
          {
            label: "Audit Logs",
            href: "/alpr/audit-logs",
            icon: ClipboardList,
          },
        ],
      });
    }

    // Footage — controlled by feature flag
    if (features.footage) {
      commonItems.push({ label: "Footage", href: "/videos", icon: Video });
    }

    // Map — controlled by feature flag
    if (features.map) {
      commonItems.push({ label: "Map", href: "/map", icon: Map });
    }

    // Admin-only extras
    if (userGroups.includes("xroadz_admin")) {
      return [
        ...commonItems,
        { label: "Alerts", icon: AlertTriangle, isDisabled: true },
        {
          label: "Reports",
          href: "/reports",
          icon: FileText,
          isDisabled: true,
        },
        {
          label: "Access Requests",
          href: "/access-requests",
          icon: ClipboardList,
          isDisabled: true,
        },
      ];
    }

    return commonItems;
  };

  const isActive = (href) => {
    return window.location.pathname === href;
  };

  const isParentActive = (item) => {
    if (isActive(item.href)) return true;
    if (item.subItems) {
      return item.subItems.some((subItem) => isActive(subItem.href));
    }
    return false;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[9997]"
        onClick={onClose}
      />

      {/* Sidebar */}
      <div className="fixed top-14 left-0 w-[332px] max-w-[85vw] bottom-0 bg-white shadow-xl z-[9999] overflow-y-auto animate-sidebar-slide-in">
        <div className="p-4 space-y-4">
          {/* Close button for mobile */}
          <div className="flex justify-between items-center pb-2 sm:hidden">
            <p className="text-lg font-semibold text-slate-700">Menu</p>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X size={20} color="#667085" />
            </button>
          </div>

          {/* Navigation Items */}
          <div className="pb-3">
            <p className="text-base font-semibold text-slate-500 mb-4 hidden sm:block">
              Menu
            </p>
            <div className="space-y-1">
              {getNavItems().map((item, index) => {
                const IconComponent = item.icon;
                const isExpanded = expandedItems[item.label];

                return (
                  <div key={index}>
                    {/* Main Navigation Item */}
                    {item.dropdown && item.subItems ? (
                      // Dropdown item - only toggles, doesn't navigate
                      <button
                        onClick={() => toggleDropdown(item.label)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors w-full ${
                          isParentActive(item)
                            ? "bg-[#e9f1e8]"
                            : "hover:bg-[#f9fafb]"
                        }`}
                      >
                        {IconComponent && (
                          <IconComponent size={20} className="text-[#667085]" />
                        )}
                        <span className="text-base font-medium flex-grow text-left text-[#344054]">
                          {item.label}
                        </span>
                        {isExpanded ? (
                          <ChevronUp size={20} className="text-[#64748B]" />
                        ) : (
                          <ChevronDown size={20} className="text-[#64748B]" />
                        )}
                      </button>
                    ) : (
                      // Regular navigation item
                      <a
                        href={item.href}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors 
                        
                        ${
                          isActive(item.href)
                            ? "bg-[#e9f1e8]"
                            : "hover:bg-[#f9fafb]"
                        }
                        ${item.isDisabled ? "pointer-events-none opacity-50 cursor-not-allowed" : ""} `}
                        onClick={onClose}
                      >
                        {IconComponent && (
                          <IconComponent size={20} className="text-[#667085]" />
                        )}
                        <span className="text-base font-medium flex-grow text-[#344054]">
                          {item.label}
                        </span>
                        {item.alert && (
                          <div className="px-2.5 rounded bg-emerald-700">
                            <p className="text-base font-semibold text-white">
                              0
                            </p>
                          </div>
                        )}
                      </a>
                    )}

                    {/* Sub-navigation items */}
                    {item.subItems && isExpanded && (
                      <div className="ml-4 mt-1 space-y-1 border-l-2 border-[#e9f1e8] pl-2">
                        {item.subItems.map((subItem, subIndex) => {
                          const SubIconComponent = subItem.icon;
                          return (
                            <a
                              key={subIndex}
                              href={subItem.href}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                                isActive(subItem.href)
                                  ? "bg-[#e9f1e8] text-emerald-700 font-medium"
                                  : "text-[#667085] hover:bg-[#f9fafb]"
                              }`}
                              onClick={onClose}
                            >
                              {SubIconComponent && (
                                <SubIconComponent size={16} />
                              )}
                              <span>{subItem.label}</span>
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="h-px bg-[#f2f4f7]" />

          {/* Location Selector */}
          <div className="pb-3 lg:hidden">
            <button
              onClick={() => setIsLocationOpen(!isLocationOpen)}
              className="flex items-center justify-between w-full px-3 py-2 hover:bg-[#f9fafb] rounded-lg"
              disabled={regionsLoading}
            >
              <div className="flex items-center gap-2">
                <MapPin size={18} color="#B9C0B8" />
                <span className="text-sm font-medium text-[#667085]">
                  Location:{" "}
                  {regionsLoading
                    ? "Loading..."
                    : selectedLocation?.region_name || "Select"}
                </span>
              </div>
              {!isLocationOpen ? (
                <ChevronDown size={18} color="#B9C0B8" />
              ) : (
                <ChevronUp size={18} color="#B9C0B8" />
              )}
            </button>

            {isLocationOpen && !regionsLoading && regions.length > 0 && (
              <div className="mt-2 space-y-1 px-3">
                {regions.map((region) => (
                  <button
                    key={region.region_id}
                    onClick={() => {
                      onLocationChange(region);
                      setIsLocationOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 hover:bg-[#f9fafb] text-sm rounded ${
                      selectedLocation?.region_id ===
                      region.region_id
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : "text-[#344054]"
                    }`}
                  >
                    {region.region_name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Animation styles */}
      <style jsx>{`
        @keyframes sidebar-slide-in {
          from {
            transform: translateX(-100%);
          }
          to {
            transform: translateX(0);
          }
        }

        .animate-sidebar-slide-in {
          animation: sidebar-slide-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
